import {
  BMButton,
  BMComponentsType,
  BMElevationType,
  BMNavigationBar,
  BMOverlay,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Dimensions,
  Easing,
  Image,
  ImageProps,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  PlatformOSType,
  ScrollView,
  StyleProp,
  StyleSheet,
  ViewStyle,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ModalManager, Portal, useScrollLock } from "../../../../components";
import BMView from "../../../../components/View/BMView";
import { useDismissKeyboardOnScroll } from "../../../../hooks/useDismissKeyboardOnScroll";

const viewport = Dimensions.get("window");
const scrollOffsetDropShadowThreshold = 10;

type BMButtonProps =
  | Omit<React.ComponentProps<typeof BMButton>, "size" | "stretchable">
  | undefined;
type BMNavigationBarProps = Omit<
  React.ComponentProps<typeof BMNavigationBar>,
  "showDivider" | "type" | "titleSize" | "color"
>;

type BMDrawerBaseProps = {
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  show: boolean;
  zIndex?: number;
  footerButtonProps?: BMButtonProps | [BMButtonProps, BMButtonProps];
  backdropProps?: ImageProps;
  hasOverlay?: boolean;
};

type BMDrawerDefaultProps = BMDrawerBaseProps & {
  variant?: "DEFAULT";
  onClose: () => void;
};

type BMDrawerCompactProps = BMDrawerBaseProps & {
  variant: "COMPACT";
  headerNavigationProps: BMNavigationBarProps;
};

type BMDrawerProps = PropsWithChildren<
  BMDrawerDefaultProps | BMDrawerCompactProps
>;

const FOOTER_HEIGHT = 64;

export default function BMDrawer(props: BMDrawerProps) {
  const {
    style,
    contentStyle,
    variant = "DEFAULT",
    show,
    zIndex = 9,
    children,
    footerButtonProps,
    backdropProps,
    hasOverlay = false,
  } = props;

  const { BMComponents, BMElevation } = useBMTheme();

  const drawerID = useId();
  const insets = useSafeAreaInsets();
  useScrollLock(!ModalManager.empty);

  const [visible, setVisible] = useState(show);
  const [isHeaderElevated, setHeaderElevated] = useState(false);

  const [isRefReady, setIsRefReady] = useState(false);
  const scrollViewRef = useRef<ScrollView | null>(null);
  useDismissKeyboardOnScroll({
    containerRef: scrollViewRef,
    enabled: isRefReady,
  });

  const drawerWidth = Math.min(viewport.width, 600);
  const elevationOpacity = useRef(new Animated.Value(0)).current;
  const animatedPosition = useRef(new Animated.Value(drawerWidth)).current;

  const styles = createStyles(BMComponents, BMElevation, Platform.OS);

  const animateDrawer = useCallback(
    (toValue: number, callback?: () => void) => {
      Animated.timing(animatedPosition, {
        toValue,
        duration: BMPrimitives.BMAnimation.duration.quick,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }).start(callback);
    },
    [animatedPosition]
  );

  const animateHeaderShadow = useCallback((toValue: number) => {
    Animated.timing(elevationOpacity, {
      toValue,
      duration: BMPrimitives.BMAnimation.duration.instant,
      useNativeDriver: true,
    }).start();
  }, []);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const yOffset = event.nativeEvent.contentOffset.y;
    const overThreshold = yOffset > scrollOffsetDropShadowThreshold;
    if (overThreshold !== isHeaderElevated) {
      animateHeaderShadow(overThreshold ? 1 : 0);
      setHeaderElevated(overThreshold);
    }
  };

  useEffect(() => {
    if (show) {
      setVisible(true);
      ModalManager.add(drawerID, setVisible);
      animateDrawer(0);
    } else {
      animateDrawer(drawerWidth, () => {
        ModalManager.remove(drawerID);
        setVisible(false);
      });
    }

    if (scrollViewRef.current) setIsRefReady(true);
  }, [show, drawerID, drawerWidth, animateDrawer]);

  useEffect(() => () => ModalManager.remove(drawerID), [drawerID]);

  if (!visible) return null;

  const navigationBarProps =
    variant === "DEFAULT"
      ? {
          onCloseActionPress: (props as BMDrawerDefaultProps).onClose,
        }
      : (props as BMDrawerCompactProps).headerNavigationProps;

  const paddingBottom = Platform.select({
    android: BMPrimitives.BMSize.xl3 + insets.bottom,
    default: insets.bottom ? insets.bottom : BMPrimitives.BMSize.md,
  });

  return (
    <Portal>
      <BMView style={[styles.wrapper, { zIndex }]}>
        {hasOverlay && <BMOverlay visible={visible} dismissible={false} />}
        <Animated.View
          style={[
            styles.container,
            Platform.select({
              web: { top: 0, bottom: 0 },
              default: { height: viewport.height },
            }),
            {
              width: drawerWidth,
              transform: [{ translateX: animatedPosition }],
            },
            style,
          ]}
        >
          <BMView flex>
            <BMView
              style={[
                styles.zIndex1,
                { paddingTop: BMPrimitives.BMSize.sm + insets.top },
              ]}
            >
              <BMView style={styles.zIndex2}>
                <BMNavigationBar
                  color="DEFAULT"
                  type="COMPACT"
                  titleSize="NORMAL"
                  showDivider
                  {...navigationBarProps}
                />
              </BMView>
              <Animated.View
                pointerEvents="none"
                style={[styles.fakeShadow, { opacity: elevationOpacity }]}
              />
            </BMView>
            <ScrollView
              ref={(ref) => {
                scrollViewRef.current = ref;
                if (ref) setIsRefReady(true);
              }}
              onScroll={handleScroll}
              scrollEventThrottle={16}
              style={styles.zIndex2}
              contentContainerStyle={[
                footerButtonProps ? {} : { paddingBottom },
                contentStyle,
              ]}
              showsVerticalScrollIndicator={false}
            >
              {children}
            </ScrollView>
            {backdropProps ? (
              <BMView
                style={[
                  styles.backdrop,
                  {
                    bottom: FOOTER_HEIGHT + paddingBottom,
                  },
                ]}
              >
                <Image {...backdropProps} />
              </BMView>
            ) : null}
            <BMDrawerFooterButtons
              styles={styles}
              footerButtonProps={footerButtonProps}
            />
          </BMView>
        </Animated.View>
      </BMView>
    </Portal>
  );
}

function BMDrawerFooterButtons({
  styles,
  footerButtonProps,
}: {
  styles: ReturnType<typeof createStyles>;
  footerButtonProps?: BMButtonProps | [BMButtonProps, BMButtonProps];
}) {
  const insets = useSafeAreaInsets();
  if (!footerButtonProps) return null;

  const paddingBottom = Platform.select({
    android: BMPrimitives.BMSize.xl3 + insets.bottom,
    default: insets.bottom ? insets.bottom : BMPrimitives.BMSize.md,
  });

  if (Array.isArray(footerButtonProps)) {
    return (
      <BMView
        row
        spacing="sm"
        style={[styles.footerWrapper, { paddingBottom }]}
      >
        {footerButtonProps.map((buttonProps, index) =>
          buttonProps ? (
            <BMView flex key={index}>
              <BMButton size="LARGE" stretchable {...buttonProps} />
            </BMView>
          ) : (
            <BMView flex key={index} />
          )
        )}
      </BMView>
    );
  }

  return (
    <BMView style={[styles.footerWrapper, { paddingBottom }]}>
      <BMButton size="LARGE" stretchable {...footerButtonProps} />
    </BMView>
  );
}

const createStyles = (
  BMComponent: BMComponentsType,
  BMElevation: BMElevationType,
  platform: PlatformOSType
) =>
  StyleSheet.create({
    // @ts-ignore platform check is needed for web compatibility
    wrapper: {
      ...StyleSheet.absoluteFillObject,
      ...(platform === "web" ? { position: "fixed" } : {}),
    },
    container: {
      right: 0,
      position: "absolute",
      backgroundColor: BMComponent.BMStyleSurfaceBgcolor,
      ...BMElevation.elevation1,
    },
    zIndex1: {
      zIndex: 1,
    },
    zIndex2: {
      zIndex: 2,
    },
    backdrop: {
      zIndex: 1,
      width: "100%",
      position: "absolute",
    },
    footerWrapper: {
      paddingTop: BMPrimitives.BMSize.lg,
      paddingHorizontal: BMPrimitives.BMSize.md,
      backgroundColor: "white",
      ...BMElevation.elevation2,
    },
    fakeShadow: {
      zIndex: 1,
      backgroundColor: "white",
      ...StyleSheet.absoluteFillObject,
      ...BMElevation.elevation2,
    },
  });
